import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/ToastContainer';
import { useToast } from './hooks/useToast';
import { AuthNavigator } from './components/AuthNavigator';

import LandingPage from './pages/LandingPage';
import DashboardLayout from './components/DashboardLayout';
import UserDashboardLayout from './components/UserDashboardLayout';

//2FA
import SMSTwoFactorSetupPage from './pages/auth/SMSTwoFactorSetUpPage';

// User pages
import AccountDetailsPage from './pages/user/AccountDetailsPage';
import SubscriptionPage from './pages/user/SubscriptionPage';
import ChangePasswordPage from './pages/user/ChangePasswordPage';
import DownloadAppPage from './pages/user/DownloadAppPage';
import FeedbackPage from './pages/user/FeedbackPage';
import SecurityPage from './pages/user/SecurityPage';

// Admin pages
import DashboardPage from './pages/admin/DashboardPage';
import UsersPage from './pages/admin/UsersPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import MessagesPage from './pages/admin/MessagesPage';
import ReleasesPage from './pages/admin/ReleasesPage';

// Public pages
import PricingPage from './pages/PricingPage';
import CheckoutPage from './pages/CheckoutPage';
import CreateAccountPage from './pages/auth/CreateAccountPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import AuthActionPage from './pages/auth/AuthActionPage';
import LoginPage from './pages/auth/LoginPage';

// CMS pages
import CMSDashboardPage from './pages/admin/cms/CMSDashboardPage';
import HeroEditorPage from './pages/admin/cms/HeroEditorPage';
import FeaturesEditorPage from './pages/admin/cms/FeaturesEditorPage';
import HowItWorksEditorPage from './pages/admin/cms/HowItWorksEditorPage';
import ContactEditorPage from './pages/admin/cms/ContactEditorPage';
import TestimonialsEditorPage from './pages/admin/cms/TestimonialsEditorPage';
import PricingEditorPage from './pages/admin/cms/PricingEditorPage';

// Shared Loading Screen
const FullPageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Admin-only guard
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <FullPageLoader />;

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// User-only guard (blocks admins)
const UserProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <FullPageLoader />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If admin tries to access user dashboard, send to admin dashboard
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
};

// Toast Container Wrapper (needs to be inside ToastProvider)
const ToastContainerWrapper: React.FC = () => {
  const { toasts } = useToast();
  return <ToastContainer toasts={toasts} />;
};

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          {/* AuthNavigator handles dashboard redirects and prevents flash */}
          <AuthNavigator />

          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/create-account" element={<CreateAccountPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/auth-action" element={<AuthActionPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup-sms-2fa" element={<SMSTwoFactorSetupPage />} />

            {/* User Routes (Nested Tabs) */}
            <Route
              path="/dashboard"
              element={
                <UserProtectedRoute>
                  <UserDashboardLayout />
                </UserProtectedRoute>
              }
            >
              <Route index element={<Navigate to="account" replace />} />
              <Route path="account" element={<AccountDetailsPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="password" element={<ChangePasswordPage />} />
              <Route path="download" element={<DownloadAppPage />} />
              <Route path="support" element={<FeedbackPage />} />
            </Route>

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <DashboardLayout />
                </AdminProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="audit-logs" element={<AuditLogsPage />} />
              <Route path="contact-submissions" element={<MessagesPage />} />
              <Route path="releases" element={<ReleasesPage />} />

              {/* CMS Routes */}
              <Route path="cms" element={<CMSDashboardPage />} />
              <Route path="cms/hero" element={<HeroEditorPage />} />
              <Route path="cms/features" element={<FeaturesEditorPage />} />
              <Route
                path="cms/how-it-works"
                element={<HowItWorksEditorPage />}
              />
              <Route path="cms/pricing" element={<PricingEditorPage />} />
              <Route path="cms/contact" element={<ContactEditorPage />} />
              <Route
                path="cms/testimonials"
                element={<TestimonialsEditorPage />}
              />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <ToastContainerWrapper />
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
